import MedicationService from '@/services/medication';

// Medication store module
//
// Owns the medication-domain reference datasets used across the Formulary
// Management module: the full medication list and medication categories. These
// are loaded ONCE when the user enters the module (via a router guard
// dispatching `setMedicationModuleData`)
// and then read by pages/components through `mapGetters` — e.g. MedicationSearch
// filters `allMedications` client-side instead of hitting the server per
// keystroke.
//
// Matches the target application's module convention: no `namespaced`, object
// `state`, SCREAMING_SNAKE mutations paired with a `*_LOADED` flag, `setX`
// actions returning a Promise, and `x` / `xLoaded` getters.

export default {
  state: {
    allMedications: [],
    allMedicationsLoaded: false,
    medicationCategories: [],
    medicationCategoriesLoaded: false,
  },

  mutations: {
    SET_ALL_MEDICATIONS(state, allMedications) {
      state.allMedications = allMedications;
    },
    SET_ALL_MEDICATIONS_LOADED(state, loaded) {
      state.allMedicationsLoaded = loaded;
    },
    SET_MEDICATION_CATEGORIES(state, medicationCategories) {
      state.medicationCategories = medicationCategories;
    },
    SET_MEDICATION_CATEGORIES_LOADED(state, loaded) {
      state.medicationCategoriesLoaded = loaded;
    },
  },

  actions: {
    setAllMedications({ commit }) {
      commit('SET_ALL_MEDICATIONS_LOADED', false);
      commit('SET_ALL_MEDICATIONS', []);
      return new Promise((resolve, reject) => {
        MedicationService.getAllMedications()
          .then((response) => {
            // Contract #3: { medications: string[] }. Tolerates a bare array.
            const allMedications = Array.isArray(response) ? response : response.medications || [];
            commit('SET_ALL_MEDICATIONS', allMedications);
            resolve();
          })
          .catch(() => {
            reject();
          })
          .finally(() => {
            commit('SET_ALL_MEDICATIONS_LOADED', true);
          });
      });
    },
    setMedicationCategories({ commit }) {
      commit('SET_MEDICATION_CATEGORIES_LOADED', false);
      commit('SET_MEDICATION_CATEGORIES', []);
      return new Promise((resolve, reject) => {
        MedicationService.getMedicationCategories()
          .then((response) => {
            commit('SET_MEDICATION_CATEGORIES', response.medicationCategories || []);
            resolve();
          })
          .catch(() => {
            reject();
          })
          .finally(() => {
            commit('SET_MEDICATION_CATEGORIES_LOADED', true);
          });
      });
    },

    // Load every medication-domain dataset once, when the user enters the
    // Formulary Management module. Self-guarded: if everything is already
    // loaded, resolves immediately so the router guard can dispatch freely.
    setMedicationModuleData({ dispatch, getters }) {
      if (getters.medicationModuleLoaded) {
        return Promise.resolve();
      }
      return Promise.all([dispatch('setAllMedications'), dispatch('setMedicationCategories')]);
    },
  },

  getters: {
    allMedications: (state) => state.allMedications,
    allMedicationsLoaded: (state) => state.allMedicationsLoaded,
    medicationCategories: (state) => state.medicationCategories,
    medicationCategoriesLoaded: (state) => state.medicationCategoriesLoaded,

    // True once every medication-domain dataset has loaded.
    medicationModuleLoaded: (state) => state.allMedicationsLoaded && state.medicationCategoriesLoaded,
  },
};
